import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Send, Hash, Users, User, Plus, Search, 
  Smile, Paperclip, MoreVertical, X, ChevronLeft, ChevronRight,
  AtSign, Reply, Edit2, Trash2, Check, Circle, MessageCircle,
  UserPlus, Settings, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const BRONZE = '#C9A961';
const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

// Format timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

// Message Component
const MessageItem = ({ message, currentUserId, onReaction, onReply, onEdit, onDelete, users }) => {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const isOwn = message.sender_id === currentUserId;
  const isSystem = message.message_type === 'system';

  const handleSaveEdit = async () => {
    if (editContent.trim() && editContent !== message.content) {
      await onEdit(message.id, editContent);
    }
    setIsEditing(false);
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <span className="text-gray-500 text-sm bg-gray-800/50 px-4 py-1 rounded-full">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div 
      className={`group flex gap-3 px-4 py-2 hover:bg-white/5 transition-colors ${showActions ? 'bg-white/5' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <Avatar className="h-10 w-10 flex-shrink-0">
        <AvatarImage src={message.sender_avatar} />
        <AvatarFallback style={{ backgroundColor: BRONZE }} className="text-black font-bold">
          {message.sender_name?.charAt(0)?.toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white">{message.sender_name}</span>
          <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
          {message.is_edited && <span className="text-xs text-gray-500">(edited)</span>}
        </div>
        
        {isEditing ? (
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="flex-1 bg-gray-800 border-gray-700"
              autoFocus
            />
            <Button size="sm" onClick={handleSaveEdit} style={{ backgroundColor: BRONZE }} className="text-black">
              <Check className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <p className="text-gray-200 break-words whitespace-pre-wrap">{message.content}</p>
            
            {/* File attachment */}
            {message.file_url && (
              <div className="mt-2">
                {message.message_type === 'image' ? (
                  <img src={message.file_url} alt={message.file_name} className="max-w-sm rounded-lg" />
                ) : (
                  <a 
                    href={message.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm px-3 py-2 bg-gray-800 rounded-lg hover:bg-gray-700"
                    style={{ color: BRONZE }}
                  >
                    <Paperclip className="h-4 w-4" />
                    {message.file_name}
                  </a>
                )}
              </div>
            )}
            
            {/* Reactions */}
            {Object.keys(message.reactions || {}).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(message.reactions).map(([emoji, userIds]) => (
                  <button
                    key={emoji}
                    onClick={() => onReaction(message.id, emoji)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm transition-colors ${
                      userIds.includes(currentUserId) 
                        ? 'bg-[#C9A961]/30 border border-[#C9A961]' 
                        : 'bg-gray-800 hover:bg-gray-700'
                    }`}
                  >
                    <span>{emoji}</span>
                    <span className="text-gray-400">{userIds.length}</span>
                  </button>
                ))}
              </div>
            )}
            
            {/* Thread count */}
            {message.thread_count > 0 && (
              <button 
                onClick={() => onReply(message)}
                className="flex items-center gap-1 mt-2 text-sm hover:underline"
                style={{ color: BRONZE }}
              >
                <MessageCircle className="h-4 w-4" />
                {message.thread_count} {message.thread_count === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Action buttons */}
      {showActions && !isEditing && (
        <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                <Smile className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2 bg-gray-900 border-gray-700">
              <div className="flex gap-1">
                {EMOJI_LIST.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => onReaction(message.id, emoji)}
                    className="text-xl hover:bg-gray-800 p-1 rounded"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onReply(message)}>
            <Reply className="h-4 w-4" />
          </Button>
          
          {isOwn && (
            <>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-300" onClick={() => onDelete(message.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Channel List Sidebar
const ChannelList = ({ channels, selectedChannel, onSelectChannel, onCreateChannel, users }) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelType, setNewChannelType] = useState('channel');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const publicChannels = channels.filter(c => c.type === 'channel');
  const directMessages = channels.filter(c => c.type === 'direct');
  const groupChats = channels.filter(c => c.type === 'group');

  const handleCreate = async () => {
    if (newChannelType === 'channel' && !newChannelName.trim()) {
      toast.error('Please enter a channel name');
      return;
    }
    if ((newChannelType === 'direct' || newChannelType === 'group') && selectedMembers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }
    
    await onCreateChannel({
      name: newChannelName || null,
      type: newChannelType,
      member_ids: selectedMembers,
      is_private: newChannelType === 'group'
    });
    
    setShowCreateDialog(false);
    setNewChannelName('');
    setSelectedMembers([]);
  };

  return (
    <div className="w-64 bg-black/60 border-r border-[#C9A961]/20 flex flex-col h-full">
      {/* Search */}
      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-black/50 border-gray-700 text-white"
          />
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        {/* AI Operations Divisions - Prominent at top */}
        {(() => {
          const divisionChannels = publicChannels.filter(c => c.is_division);
          if (divisionChannels.length > 0) {
            return (
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-[#C9A961] uppercase tracking-wider">🤖 AI Divisions</span>
                </div>
                {divisionChannels.map(channel => {
                  const divisionColors = {
                    government: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-400' },
                    gc: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'text-orange-400' },
                    fm: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: 'text-green-400' },
                  };
                  const colors = divisionColors[channel.division_id] || { bg: 'bg-gray-800', border: 'border-gray-700', icon: 'text-gray-400' };
                  
                  return (
                    <button
                      key={channel.id}
                      onClick={() => onSelectChannel(channel)}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-left transition-colors mb-1 border ${
                        selectedChannel?.id === channel.id 
                          ? `${colors.bg} ${colors.border} text-white` 
                          : `${colors.bg} ${colors.border} text-gray-300 hover:text-white`
                      }`}
                    >
                      <Hash className={`h-4 w-4 flex-shrink-0 ${colors.icon}`} />
                      <span className="truncate flex-1 font-medium">{channel.name}</span>
                      {channel.unread_count > 0 && (
                        <Badge className="ml-auto bg-[#C9A961] text-black text-xs">
                          {channel.unread_count}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          }
          return null;
        })()}
        
        {/* Regular Channels */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Channels</span>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 w-6 p-0"
              onClick={() => { setNewChannelType('channel'); setShowCreateDialog(true); }}
            >
              <Plus className="h-4 w-4" style={{ color: BRONZE }} />
            </Button>
          </div>
          {publicChannels.filter(c => !c.is_division).map(channel => (
            <button
              key={channel.id}
              onClick={() => onSelectChannel(channel)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                selectedChannel?.id === channel.id 
                  ? 'bg-[#C9A961]/20 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Hash className="h-4 w-4 flex-shrink-0" />
              <span className="truncate flex-1">{channel.name}</span>
              {channel.unread_count > 0 && (
                <Badge className="ml-auto bg-[#C9A961] text-black text-xs">
                  {channel.unread_count}
                </Badge>
              )}
            </button>
          ))}
        </div>
        
        {/* Direct Messages */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Direct Messages</span>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 w-6 p-0"
              onClick={() => { setNewChannelType('direct'); setShowCreateDialog(true); }}
            >
              <Plus className="h-4 w-4" style={{ color: BRONZE }} />
            </Button>
          </div>
          {directMessages.map(channel => (
            <button
              key={channel.id}
              onClick={() => onSelectChannel(channel)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                selectedChannel?.id === channel.id 
                  ? 'bg-[#C9A961]/20 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <div className="relative">
                <Avatar className="h-6 w-6">
                  <AvatarFallback style={{ backgroundColor: BRONZE }} className="text-black text-xs">
                    {channel.other_user?.name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <Circle 
                  className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 ${
                    channel.other_user?.online_status === 'online' ? 'fill-green-500 text-green-500' : 'fill-gray-500 text-gray-500'
                  }`}
                />
              </div>
              <span className="truncate flex-1">{channel.other_user?.name || channel.name}</span>
              {channel.unread_count > 0 && (
                <Badge className="ml-auto bg-[#C9A961] text-black text-xs">
                  {channel.unread_count}
                </Badge>
              )}
            </button>
          ))}
        </div>
        
        {/* Group Chats */}
        {groupChats.length > 0 && (
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Groups</span>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-6 w-6 p-0"
                onClick={() => { setNewChannelType('group'); setShowCreateDialog(true); }}
              >
                <Plus className="h-4 w-4" style={{ color: BRONZE }} />
              </Button>
            </div>
            {groupChats.map(channel => (
              <button
                key={channel.id}
                onClick={() => onSelectChannel(channel)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                  selectedChannel?.id === channel.id 
                    ? 'bg-[#C9A961]/20 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <Users className="h-4 w-4 flex-shrink-0" />
                <span className="truncate flex-1">{channel.name}</span>
                {channel.unread_count > 0 && (
                  <Badge className="ml-auto bg-[#C9A961] text-black text-xs">
                    {channel.unread_count}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
      
      {/* Create Channel Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle style={{ color: BRONZE }}>
              {newChannelType === 'channel' ? 'Create Channel' : 
               newChannelType === 'direct' ? 'Start Direct Message' : 'Create Group Chat'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {newChannelType === 'channel' && (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Channel Name</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">#</span>
                  <Input
                    placeholder="general"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value.toLowerCase().replace(/\s/g, '-'))}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
              </div>
            )}
            
            {newChannelType === 'group' && (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Group Name</label>
                <Input
                  placeholder="Project Team"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
            )}
            
            {(newChannelType === 'direct' || newChannelType === 'group') && (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  {newChannelType === 'direct' ? 'Select User' : 'Select Members'}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => {
                        if (newChannelType === 'direct') {
                          setSelectedMembers([user.id]);
                        } else {
                          setSelectedMembers(prev => 
                            prev.includes(user.id) 
                              ? prev.filter(id => id !== user.id)
                              : [...prev, user.id]
                          );
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        selectedMembers.includes(user.id) 
                          ? 'bg-[#C9A961]/20 border border-[#C9A961]' 
                          : 'bg-gray-800 hover:bg-gray-700'
                      }`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback style={{ backgroundColor: BRONZE }} className="text-black">
                          {user.first_name?.charAt(0) || user.username?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="text-white text-sm">{user.first_name} {user.last_name}</p>
                        <p className="text-gray-500 text-xs">@{user.username}</p>
                      </div>
                      {selectedMembers.includes(user.id) && (
                        <Check className="h-4 w-4 ml-auto" style={{ color: BRONZE }} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              onClick={handleCreate}
              className="w-full"
              style={{ backgroundColor: BRONZE, color: 'black' }}
            >
              {newChannelType === 'channel' ? 'Create Channel' : 
               newChannelType === 'direct' ? 'Start Chat' : 'Create Group'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ChatPage = () => {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize WebSocket
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${currentUser.id}`);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'new_message':
            if (data.message.channel_id === selectedChannel?.id) {
              setMessages(prev => [...prev, data.message]);
            }
            setChannels(prev => prev.map(ch => 
              ch.id === data.message.channel_id 
                ? { ...ch, last_message_preview: data.message.content, last_message_at: data.message.created_at }
                : ch
            ));
            break;
          case 'message_edited':
            setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
            break;
          case 'message_deleted':
            setMessages(prev => prev.filter(m => m.id !== data.message_id));
            break;
          case 'reaction_updated':
            setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
            break;
          case 'typing':
            if (data.channel_id === selectedChannel?.id && data.user_id !== currentUser.id) {
              setTypingUsers(prev => [...new Set([...prev, data.user_id])]);
              setTimeout(() => {
                setTypingUsers(prev => prev.filter(id => id !== data.user_id));
              }, 3000);
            }
            break;
          default:
            break;
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected, reconnecting...');
        setTimeout(connectWebSocket, 3000);
      };
    };
    
    if (currentUser.id) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [currentUser.id, selectedChannel?.id]);

  // Fetch channels and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsRes, usersRes] = await Promise.all([
          api.get('/chat/channels'),
          api.get('/chat/users')
        ]);
        setChannels(channelsRes.data);
        setUsers(usersRes.data);
        
        if (channelsRes.data.length > 0 && !selectedChannel) {
          setSelectedChannel(channelsRes.data[0]);
        }
      } catch (error) {
        console.error('Error fetching chat data:', error);
        if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response.data.length === 0)) {
          try {
            await api.post('/chat/init');
            const channelsRes = await api.get('/chat/channels');
            setChannels(channelsRes.data);
            if (channelsRes.data.length > 0) {
              setSelectedChannel(channelsRes.data[0]);
            }
          } catch (initError) {
            console.error('Error initializing channels:', initError);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch messages when channel changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedChannel) return;
      
      try {
        const res = await api.get(`/chat/channels/${selectedChannel.id}/messages`);
        setMessages(res.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
        toast.error('Failed to load messages');
      }
    };
    
    fetchMessages();
  }, [selectedChannel]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChannel) return;
    
    try {
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(newMessage)) !== null) {
        const user = users.find(u => u.username === match[1]);
        if (user) mentions.push(user.id);
      }
      
      await api.post('/chat/messages', {
        content: newMessage,
        channel_id: selectedChannel.id,
        parent_id: replyingTo?.id || null,
        message_type: 'text',
        mentions
      });
      
      setNewMessage('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        channel_id: selectedChannel?.id
      }));
    }
  };

  // Add reaction
  const handleReaction = async (messageId, emoji) => {
    try {
      await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Edit message
  const handleEditMessage = async (messageId, content) => {
    try {
      await api.put(`/chat/messages/${messageId}`, { content });
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    try {
      await api.delete(`/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  // Create channel
  const handleCreateChannel = async (channelData) => {
    try {
      if (channelData.type === 'direct') {
        const res = await api.post('/chat/direct', { user_id: channelData.member_ids[0] });
        setChannels(prev => {
          const exists = prev.find(c => c.id === res.data.id);
          return exists ? prev : [res.data, ...prev];
        });
        setSelectedChannel(res.data);
      } else {
        const res = await api.post('/chat/channels', channelData);
        setChannels(prev => [res.data, ...prev]);
        setSelectedChannel(res.data);
      }
      toast.success('Created successfully!');
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error(error.response?.data?.detail || 'Failed to create');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full" style={{ borderColor: BRONZE }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header - Matching Video Meetings style */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-16" />
          <div>
            <h1 className="text-4xl font-bold font-serif flex items-center gap-3">
              <MessageSquare className="h-10 w-10" style={{ color: BRONZE }} />
              <span className="text-white">Team</span>{' '}
              <span className="italic" style={{ color: BRONZE }}>Chat</span>
            </h1>
            <p className="text-gray-400 text-lg">Real-time messaging for your team</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button 
            onClick={() => window.open('/chat', 'TeamChat_Main', 'width=1200,height=900,left=100,top=100')}
            variant="outline"
            className="border-[#C9A961] text-[#C9A961]"
            title="Open in new window"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            Pop Out
          </Button>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            style={{ backgroundColor: BRONZE }}
            className="text-black"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Channel
          </Button>
          
          <Button variant="outline" className="border-[#C9A961] text-[#C9A961]">
            <UserPlus className="h-5 w-5 mr-2" />
            Invite Members
          </Button>
        </div>
      </div>

      {/* Quick Actions - Matching Video Meetings style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="bg-gradient-to-br from-[#C9A961]/20 to-black border-[#C9A961]/30 cursor-pointer hover:border-[#C9A961] transition-all"
          onClick={() => {
            const general = channels.find(c => c.name === 'general');
            if (general) setSelectedChannel(general);
          }}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Hash className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">General Chat</h3>
              <p className="text-gray-400 text-sm">Join the general discussion</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="bg-gradient-to-br from-[#C9A961]/10 to-black border-[#C9A961]/20 cursor-pointer hover:border-[#C9A961] transition-all"
          onClick={() => setShowCreateDialog(true)}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Users className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">Create Group</h3>
              <p className="text-gray-400 text-sm">Start a private group chat</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#C9A961]/10 to-black border-[#C9A961]/20 cursor-pointer hover:border-[#C9A961] transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Bell className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">Announcements</h3>
              <p className="text-gray-400 text-sm">View company updates</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Chat Area */}
      <div className="flex h-[calc(100vh-380px)] bg-black/50 rounded-xl overflow-hidden border border-[#C9A961]/20">
        {/* Channel Sidebar */}
        <ChannelList
          channels={channels}
          selectedChannel={selectedChannel}
          onSelectChannel={setSelectedChannel}
          onCreateChannel={handleCreateChannel}
          users={users}
        />
        
        {/* Chat Content */}
        <div className="flex-1 flex flex-col bg-black/30">
          {selectedChannel ? (
            <>
              {/* Channel Header */}
              <div className="px-4 py-3 border-b border-[#C9A961]/20 flex items-center justify-between bg-black/50">
                <div className="flex items-center gap-3">
                  {selectedChannel.type === 'channel' && <Hash className="h-5 w-5" style={{ color: BRONZE }} />}
                  {selectedChannel.type === 'direct' && (
                    <Avatar className="h-8 w-8">
                      <AvatarFallback style={{ backgroundColor: BRONZE }} className="text-black">
                        {selectedChannel.other_user?.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {selectedChannel.type === 'group' && <Users className="h-5 w-5" style={{ color: BRONZE }} />}
                  <div>
                    <h3 className="font-semibold text-white">
                      {selectedChannel.type === 'direct' ? selectedChannel.other_user?.name : selectedChannel.name}
                    </h3>
                    {selectedChannel.description && (
                      <p className="text-sm text-gray-500">{selectedChannel.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50">
                    {selectedChannel.member_ids?.length || 0} members
                  </Badge>
                </div>
              </div>
              
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <MessageSquare className="h-12 w-12 mb-4" style={{ color: BRONZE }} />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {messages.map(message => (
                      <MessageItem
                        key={message.id}
                        message={message}
                        currentUserId={currentUser.id}
                        onReaction={handleReaction}
                        onReply={setReplyingTo}
                        onEdit={handleEditMessage}
                        onDelete={handleDeleteMessage}
                        users={users}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Typing indicator */}
              {typingUsers.length > 0 && (
                <div className="px-4 py-1 text-sm text-gray-500">
                  Someone is typing...
                </div>
              )}
              
              {/* Reply indicator */}
              {replyingTo && (
                <div className="px-4 py-2 bg-gray-800/50 border-t border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Reply className="h-4 w-4" style={{ color: BRONZE }} />
                    <span className="text-sm text-gray-400">
                      Replying to <span className="text-white">{replyingTo.sender_name}</span>
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setReplyingTo(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-[#C9A961]/20 bg-black/50">
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                    <Paperclip className="h-5 w-5" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      handleTyping();
                    }}
                    placeholder={`Message ${selectedChannel.type === 'channel' ? '#' : ''}${selectedChannel.name || selectedChannel.other_user?.name || ''}`}
                    className="flex-1 bg-black/50 border-gray-700 text-white"
                  />
                  <Button type="button" variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                    <AtSign className="h-5 w-5" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                    <Smile className="h-5 w-5" />
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={!newMessage.trim()}
                    style={{ backgroundColor: BRONZE }}
                    className="text-black"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4" style={{ color: BRONZE }} />
                <h3 className="text-xl font-semibold text-white mb-2">Welcome to Team Chat</h3>
                <p>Select a channel or start a new conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Channel Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: BRONZE }}>Create New Channel</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="channel" className="w-full">
            <TabsList className="bg-black/50 border border-gray-700 w-full">
              <TabsTrigger 
                value="channel" 
                className="flex-1 data-[state=active]:bg-[#C9A961] data-[state=active]:text-black"
              >
                Channel
              </TabsTrigger>
              <TabsTrigger 
                value="group"
                className="flex-1 data-[state=active]:bg-[#C9A961] data-[state=active]:text-black"
              >
                Group Chat
              </TabsTrigger>
              <TabsTrigger 
                value="dm"
                className="flex-1 data-[state=active]:bg-[#C9A961] data-[state=active]:text-black"
              >
                Direct Message
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="channel" className="mt-4 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Channel Name</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">#</span>
                  <Input
                    placeholder="e.g., project-alpha"
                    className="bg-black/50 border-gray-700"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description (Optional)</label>
                <Input
                  placeholder="What's this channel about?"
                  className="bg-black/50 border-gray-700"
                />
              </div>
              <Button 
                className="w-full"
                style={{ backgroundColor: BRONZE, color: 'black' }}
                onClick={() => setShowCreateDialog(false)}
              >
                Create Channel
              </Button>
            </TabsContent>
            
            <TabsContent value="group" className="mt-4 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Group Name</label>
                <Input
                  placeholder="e.g., Project Team"
                  className="bg-black/50 border-gray-700"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Add Members</label>
                <ScrollArea className="h-32 border border-gray-700 rounded-md p-2">
                  {users.map(user => (
                    <div 
                      key={user.id}
                      className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800"
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold" style={{ backgroundColor: BRONZE }}>
                        {user.first_name?.charAt(0) || user.username?.charAt(0)}
                      </div>
                      <span className="text-white text-sm">{user.first_name} {user.last_name}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              <Button 
                className="w-full"
                style={{ backgroundColor: BRONZE, color: 'black' }}
                onClick={() => setShowCreateDialog(false)}
              >
                Create Group
              </Button>
            </TabsContent>
            
            <TabsContent value="dm" className="mt-4 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Select User</label>
                <ScrollArea className="h-48 border border-gray-700 rounded-md p-2">
                  {users.map(user => (
                    <div 
                      key={user.id}
                      className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800"
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold" style={{ backgroundColor: BRONZE }}>
                        {user.first_name?.charAt(0) || user.username?.charAt(0)}
                      </div>
                      <div>
                        <span className="text-white text-sm block">{user.first_name} {user.last_name}</span>
                        <span className="text-gray-500 text-xs">@{user.username}</span>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              <Button 
                className="w-full"
                style={{ backgroundColor: BRONZE, color: 'black' }}
                onClick={() => setShowCreateDialog(false)}
              >
                Start Conversation
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
